import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { Check } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Trans } from 'react-i18next';
import { Combobox, type ComboItem } from '@/components/combobox';
import { ErrorBanner } from '@/components/form';
import { FileDrop, StatusChip } from '@/components/migration/file-drop';
import { GetFromQuickBooks } from '@/components/migration/quickbooks';
import { EmptyState, PageHeader } from '@/components/page-header';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useAccounts } from '@/lib/accounts';
import { api } from '@/lib/api';
import { ltr, nameFor } from '@/lib/arabic';
import { useMe } from '@/lib/clients';
import { formatDate } from '@/lib/format';
import { useLanguage, useT } from '@/lib/i18n';
import {
  fmtAmount,
  mappedTotals,
  readUpload,
  useMigration,
  type MigrationView,
  type TrialBalanceLine,
  type TrialBalancePreview,
} from '@/lib/migration';
import { formatMinor } from '@/lib/money';
import { useOrgId } from '@/lib/org';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/settings/migration/opening-balances')({
  component: OpeningBalancesPage,
});

type Step = 'upload' | 'map' | 'review' | 'done';
const STEPS: Step[] = ['upload', 'map', 'review', 'done'];

/** Screen G8 (document 06): the opening trial balance from QuickBooks. */
function OpeningBalancesPage() {
  const orgId = useOrgId();
  const me = useMe();
  const m = useMigration();
  const t = useT();
  return (
    <>
      <PageHeader
        title={t('settingsPages.migration.openingBalances.title')}
        description={t('settingsPages.migration.openingBalances.description')}
        actions={
          <Button variant="outline" asChild>
            <Link to="/settings/migration">{t('settingsPages.migration.moveFromQuickBooks')}</Link>
          </Button>
        }
      />
      {!orgId ? (
        <EmptyState title={t('shell.selectClient')} hint={t('home.selectClientHint')} />
      ) : me.data && !me.data.isStaff ? (
        <EmptyState title={t('settingsPages.migration.openingBalances.staffOnly')} />
      ) : m.isPending ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : m.isError ? (
        <EmptyState title={t('settingsPages.migration.couldNotLoad')} hint={m.error.message} />
      ) : !m.data.conversionDate ? (
        <EmptyState
          title={t('settingsPages.migration.setDateFirst')}
          hint={t('settingsPages.migration.openingBalances.setDateFirstHint')}
          action={
            <Button asChild>
              <Link to="/settings/migration">
                {t('settingsPages.migration.moveFromQuickBooks')}
              </Link>
            </Button>
          }
        />
      ) : m.data.status === 'finished' ? (
        <EmptyState
          title={t('settingsPages.migration.finished')}
          hint={t('settingsPages.migration.openingBalances.finishedHint')}
        />
      ) : (
        <Wizard key={orgId} view={m.data} />
      )}
    </>
  );
}

function Wizard({ view }: { view: MigrationView }) {
  const orgId = useOrgId();
  const qc = useQueryClient();
  const accounts = useAccounts();
  const t = useT();
  const lang = useLanguage();
  const iso = (v: string) => (lang === 'ar' ? ltr(v) : v);
  const [step, setStep] = useState<Step>('upload');
  const [fileError, setFileError] = useState<string | null>(null);
  const [preview, setPreview] = useState<TrialBalancePreview | null>(null);
  const [lines, setLines] = useState<TrialBalanceLine[]>([]);
  const [posted, setPosted] = useState<MigrationView | null>(null);

  const byId = useMemo(() => new Map((accounts.data ?? []).map((a) => [a.id, a])), [accounts.data]);
  const roleOf = (id: string | null): TrialBalanceLine['role'] => {
    const type = id ? byId.get(id)?.detailType : undefined;
    return type === 'accounts_receivable'
      ? 'receivable'
      : type === 'accounts_payable'
        ? 'payable'
        : 'post';
  };
  const items: ComboItem[] = useMemo(
    () =>
      (accounts.data ?? [])
        .filter((a) => a.isActive)
        .map((a) => ({
          id: a.id,
          label: `${a.code} ${nameFor(lang, a.fullName, a.fullNameAr)}`,
          keywords: `${a.accountTypeName} ${a.fullName} ${a.fullNameAr ?? ''}`,
          group: nameFor(lang, a.accountTypeName, a.accountTypeNameAr),
        })),
    [accounts.data, lang],
  );

  const read = useMutation({
    mutationFn: async (f: File) => {
      const file = await readUpload(f);
      return api<TrialBalancePreview>(`/orgs/${orgId}/migration/trial-balance/preview`, {
        method: 'POST',
        body: JSON.stringify({ file }),
      });
    },
    onMutate: () => setFileError(null),
    onSuccess: (p) => {
      setPreview(p);
      setLines(p.lines);
      setStep('map');
    },
    onError: (e) => setFileError((e as Error).message),
  });

  const post = useMutation({
    mutationFn: () =>
      api<MigrationView>(`/orgs/${orgId}/migration/trial-balance`, {
        method: 'POST',
        body: JSON.stringify({
          fileName: preview!.fileName,
          asOf: preview!.asOf,
          lines: lines.map((l) => ({
            label: l.label,
            debit: l.debit,
            credit: l.credit,
            accountId: l.accountId,
          })),
        }),
      }),
    onSuccess: async (v) => {
      setPosted(v);
      setStep('done');
      qc.setQueryData(['migration', orgId], v);
      await qc.invalidateQueries({ queryKey: ['accounts', orgId] });
    },
  });

  const totals = mappedTotals(lines, roleOf);
  const balanced = totals.debit === totals.credit;
  const at = STEPS.indexOf(step);

  return (
    <div className="flex max-w-6xl flex-col gap-4">
      <ol
        className="flex items-center gap-2 text-sm"
        aria-label={t('settingsPages.migration.openingBalances.stepsLabel')}
      >
        {STEPS.map((s, i) => (
          <li key={s} className="flex items-center gap-2">
            <span
              className={cn(
                'flex size-6 items-center justify-center rounded-full border text-xs',
                i < at && 'border-primary bg-primary text-primary-foreground',
                i === at && 'border-primary text-primary',
                i > at && 'text-muted-foreground',
              )}
            >
              {i < at ? <Check className="size-3.5" /> : i + 1}
            </span>
            <span
              className={cn(i === at ? 'font-medium' : 'text-muted-foreground')}
              aria-current={i === at ? 'step' : undefined}
            >
              {t(`settingsPages.migration.openingBalances.step.${s}`)}
            </span>
            {i < STEPS.length - 1 ? <span className="mx-1 h-px w-8 bg-border" /> : null}
          </li>
        ))}
      </ol>

      {step === 'upload' ? (
        <>
          {view.trialBalance ? (
            <div className="rounded-lg border bg-card px-4 py-3 text-sm" role="status">
              <Trans
                t={t}
                i18nKey={
                  view.trialBalance.fileName
                    ? 'settingsPages.openingBalancesPosted.postedAsFrom'
                    : 'settingsPages.openingBalancesPosted.postedAs'
                }
                values={{
                  entryNo: iso(view.trialBalance.entryNo),
                  fileName: iso(view.trialBalance.fileName ?? ''),
                }}
                components={{
                  lnk: (
                    <Link
                      to="/accounting/journal-entries/$entryId"
                      params={{ entryId: view.trialBalance.entryId }}
                      className="text-primary hover:underline"
                    />
                  ),
                }}
              />
            </div>
          ) : null}
          <ErrorBanner>{fileError}</ErrorBanner>
          <FileDrop
            title={t('settingsPages.migration.openingBalances.dropTitle')}
            hint={
              <Trans
                t={t}
                i18nKey="settingsPages.migration.openingBalances.dropHint"
                values={{ date: formatDate(view.openingDate!) }}
                components={{ b: <strong /> }}
              />
            }
            busy={read.isPending}
            onFile={(f) => read.mutate(f)}
          />
          <GetFromQuickBooks kind="trial-balance" onFile={(f) => read.mutate(f)} />
        </>
      ) : null}

      {step === 'map' && preview ? (
        <>
          <ErrorBanner>
            {preview.issues.length
              ? preview.issues
                  .map((i) =>
                    i.row
                      ? t('settingsPages.migration.rowIssue', { row: i.row, message: i.message })
                      : i.message,
                  )
                  .join(' · ')
              : null}
          </ErrorBanner>
          <p className="text-sm text-muted-foreground">
            <span className="font-medium text-foreground">
              <bdi>{preview.fileName}</bdi>
            </span>
            {' · '}
            {preview.asOf
              ? t('settingsPages.migration.openingBalances.fileSummaryAsOf', {
                  count: lines.length,
                  date: formatDate(preview.asOf),
                })
              : t('settingsPages.migration.openingBalances.fileSummary', { count: lines.length })}
            {preview.asOf && preview.asOf !== view.openingDate ? (
              <span className="text-warning">
                {' '}
                {t('settingsPages.migration.openingBalances.notAsOf', {
                  date: formatDate(view.openingDate!),
                })}
              </span>
            ) : null}{' '}
            {t('settingsPages.migration.openingBalances.matchedNote')}
          </p>
          <div className="max-h-[560px] overflow-y-auto rounded-lg border bg-card">
            <Table>
              <TableHeader className="sticky top-0 bg-card">
                <TableRow>
                  <TableHead>{t('settingsPages.migration.openingBalances.qbAccount')}</TableHead>
                  <TableHead className="w-[130px] text-end">
                    {t('settingsPages.migration.debit')}
                  </TableHead>
                  <TableHead className="w-[130px] text-end">
                    {t('settingsPages.migration.credit')}
                  </TableHead>
                  <TableHead className="w-[340px]">
                    {t('settingsPages.migration.openingBalances.ledgerAccount')}
                  </TableHead>
                  <TableHead className="w-[140px]">
                    {t('settingsPages.migration.openingBalances.matchedBy')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {lines.map((l, i) => {
                  const role = roleOf(l.accountId);
                  return (
                    <TableRow key={`${l.row}-${l.label}`}>
                      <TableCell className="font-medium">
                        {l.label}
                        {role !== 'post' ? (
                          <div className="text-xs font-normal text-muted-foreground">
                            {role === 'receivable'
                              ? t('settingsPages.migration.openingBalances.throughInvoices')
                              : t('settingsPages.migration.openingBalances.throughBills')}
                          </div>
                        ) : null}
                      </TableCell>
                      <TableCell className="tnum text-end">{fmtAmount(l.debit, true)}</TableCell>
                      <TableCell className="tnum text-end">{fmtAmount(l.credit, true)}</TableCell>
                      <TableCell>
                        <Combobox
                          items={items}
                          value={l.accountId}
                          ariaLabel={t('settingsPages.migration.openingBalances.accountFor', {
                            label: l.label,
                          })}
                          invalid={!l.accountId}
                          placeholder={t('settingsPages.migration.openingBalances.chooseAccount')}
                          onChange={(id) =>
                            setLines((prev) =>
                              prev.map((x, j) =>
                                j === i ? { ...x, accountId: id, how: null } : x,
                              ),
                            )
                          }
                        />
                      </TableCell>
                      <TableCell>
                        {l.how ? (
                          <StatusChip tone="success">
                            {t(`settingsPages.migration.openingBalances.how.${l.how}`)}
                          </StatusChip>
                        ) : l.accountId ? (
                          <StatusChip tone="info">
                            {t('settingsPages.migration.openingBalances.chosen')}
                          </StatusChip>
                        ) : (
                          <StatusChip tone="warning">
                            {t('settingsPages.migration.openingBalances.notMatched')}
                          </StatusChip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TableCell className="font-semibold">
                    {t('settingsPages.migration.openingBalances.total')}
                  </TableCell>
                  <TableCell className="tnum text-end font-semibold">
                    {formatMinor(totals.debit)}
                  </TableCell>
                  <TableCell className="tnum text-end font-semibold">
                    {formatMinor(totals.credit)}
                  </TableCell>
                  <TableCell colSpan={2} className="text-sm">
                    {!balanced
                      ? t('settingsPages.migration.openingBalances.outOfBalance', {
                          amount: formatMinor(totals.debit - totals.credit),
                        })
                      : totals.unmapped
                        ? t('settingsPages.migration.openingBalances.unmapped', {
                            count: totals.unmapped,
                          })
                        : t('settingsPages.migration.openingBalances.balanced')}
                  </TableCell>
                </TableRow>
              </TableFooter>
            </Table>
          </div>
          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setStep('upload')}>
              {t('settingsPages.migration.back')}
            </Button>
            <Button
              disabled={!balanced || totals.unmapped > 0 || lines.length < 2}
              onClick={() => setStep('review')}
            >
              {t('settingsPages.migration.next')}
            </Button>
          </div>
        </>
      ) : null}

      {step === 'review' && preview ? (
        <>
          <ErrorBanner>{post.error?.message}</ErrorBanner>
          <div className="rounded-lg border bg-card px-4 py-3 text-sm" role="status">
            <Trans
              t={t}
              i18nKey="settingsPages.migration.openingBalances.reviewSummary"
              count={lines.filter((l) => roleOf(l.accountId) === 'post').length}
              values={{ date: formatDate(view.openingDate!), amount: formatMinor(totals.awaiting) }}
              components={{ b: <strong />, amt: <strong className="tnum" /> }}
            />
            {view.trialBalance
              ? ` ${t('settingsPages.migration.openingBalances.earlierReversed')}`
              : ''}
          </div>
          <div className="rounded-lg border bg-card">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('settingsPages.migration.openingBalances.account')}</TableHead>
                  <TableHead className="w-[140px] text-end">
                    {t('settingsPages.migration.debit')}
                  </TableHead>
                  <TableHead className="w-[140px] text-end">
                    {t('settingsPages.migration.credit')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {lines
                  .filter((l) => roleOf(l.accountId) === 'post')
                  .map((l) => {
                    const a = byId.get(l.accountId!);
                    return (
                      <TableRow key={`${l.row}-${l.label}`}>
                        <TableCell>
                          {a ? `${a.code} ${nameFor(lang, a.fullName, a.fullNameAr)}` : l.label}
                        </TableCell>
                        <TableCell className="tnum text-end">{fmtAmount(l.debit, true)}</TableCell>
                        <TableCell className="tnum text-end">{fmtAmount(l.credit, true)}</TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </div>
          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setStep('map')}>
              {t('settingsPages.migration.back')}
            </Button>
            <Button disabled={post.isPending} onClick={() => post.mutate()}>
              {post.isPending
                ? t('settingsPages.migration.openingBalances.posting')
                : t('settingsPages.migration.openingBalances.post')}
            </Button>
          </div>
        </>
      ) : null}

      {step === 'done' && posted?.trialBalance ? (
        <>
          <div
            className="rounded-lg border border-primary/30 bg-primary/5 px-4 py-3 text-sm"
            role="status"
          >
            <Trans
              t={t}
              i18nKey="settingsPages.openingBalancesPosted.postedDone"
              values={{ entryNo: iso(posted.trialBalance.entryNo) }}
              components={{
                b: <span className="font-medium" />,
                lnk: (
                  <Link
                    to="/accounting/journal-entries/$entryId"
                    params={{ entryId: posted.trialBalance.entryId }}
                    className="text-primary hover:underline"
                  />
                ),
              }}
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" asChild>
              <Link to="/settings/migration">{t('settingsPages.migration.backToChecklist')}</Link>
            </Button>
            <Button asChild>
              <Link to="/settings/migration/open-items/$side" params={{ side: 'receivable' }}>
                {t('settingsPages.migration.openingBalances.importOpenInvoices')}
              </Link>
            </Button>
          </div>
        </>
      ) : null}
    </div>
  );
}
